import { Ban, KeyRound, RefreshCw, ScanLine, Store, X } from "lucide-react";

import { BrebQrPayeeCard } from "@/modules/dashboard/breb-qr-pay/components/BrebQrPayeeCard";
import type { BrebQrResolutionState } from "@/modules/dashboard/breb-qr-pay/hooks/useBrebQrResolution";
import { ColocaButton } from "@/shared/components/buttons/ColocaButton";
import { ColocaAlert } from "@/shared/components/feedback/ColocaAlert";
import { ColocaSkeleton } from "@/shared/components/feedback/ColocaSkeleton";
import useTranslations from "@/shared/lang/useTranslations";

export interface ScannedQrRecipientProps {
  state: Exclude<BrebQrResolutionState, { status: "idle" }>;
  onRetry: () => void;
  onScanAnother: () => void;
  onClear: () => void;
}

/** The recipient a scanned QR names, in place of the key field while the QR is in use. */
export function ScannedQrRecipient({ state, onRetry, onScanAnother, onClear }: ScannedQrRecipientProps) {
  const { t } = useTranslations("brebQrPay");

  const actions = (
    <div className="flex flex-wrap gap-2">
      <ColocaButton type="button" size="sm" variant="secondary" icon={ScanLine} onClick={onScanAnother}>
        {t("resolve_scan_another")}
      </ColocaButton>
      <ColocaButton type="button" size="sm" variant="ghost" icon={X} onClick={onClear}>
        {t("qr_clear")}
      </ColocaButton>
    </div>
  );

  if (state.status === "loading") {
    return (
      <div className="space-y-2" aria-busy="true">
        <p aria-live="polite" className="text-sm text-muted-foreground">
          {t("resolving")}
        </p>
        <ColocaSkeleton className="h-36 w-full rounded-2xl" />
      </div>
    );
  }

  if (state.status === "failed") {
    const copy = {
      invalid: { icon: ScanLine, title: t("resolve_invalid_title"), text: t("resolve_invalid_text") },
      key_not_found: {
        icon: KeyRound,
        title: t("resolve_key_not_found_title"),
        text: t("resolve_key_not_found_text"),
      },
      unavailable: {
        icon: RefreshCw,
        title: t("resolve_unavailable_title"),
        text: t("resolve_unavailable_text"),
      },
      access_denied: {
        icon: Ban,
        title: t("resolve_access_denied_title"),
        text: t("resolve_access_denied_text"),
      },
      unsupported: {
        icon: Store,
        title: t("resolve_unsupported_title"),
        text: t("resolve_unsupported_text"),
      },
      not_enabled: {
        icon: Ban,
        title: t("resolve_not_enabled_title"),
        text: t("resolve_not_enabled_text"),
      },
    }[state.error];
    return (
      <div className="space-y-3">
        <ColocaAlert
          variant={
            state.error === "unavailable" || state.error === "unsupported" || state.error === "not_enabled"
              ? "warning"
              : "destructive"
          }
          icon={copy.icon}
          heading={copy.title}
          text={state.error === "key_not_found" ? (state.message ?? copy.text) : copy.text}
          actions={
            state.error === "unavailable" ? (
              <ColocaButton size="sm" variant="default" icon={RefreshCw} onClick={onRetry}>
                {t("resolve_retry")}
              </ColocaButton>
            ) : undefined
          }
        />
        {actions}
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <BrebQrPayeeCard resolution={state.data} />
      {actions}
    </div>
  );
}
